// pages/ForgotPassword.tsx
import { useState, useEffect } from "react";
import { Link } from "wouter";
import Layout from "@/components/Layout";
import SEO from '@/components/SEO'; // SEO: password recovery page -> noindex,nofollow
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // cooldown tick
  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const isValidEmail = (v: string) => /.+@.+\..+/.test(v);
  const formatMMSS = (s: number) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!isValidEmail(email)) {
      setErrorMsg("Enter a valid email address");
      return;
    }
    if (cooldown > 0) return;

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Important: do not send credentials/cookies for this endpoint
        credentials: 'omit',
        body: JSON.stringify({ email }),
      });

      if (res.status === 429) {
        setErrorMsg("Too many attempts, try later");
        setCooldown(45); // 30–60s; pick a middle value
        return;
      }

      // Always show the same success message to prevent enumeration
      setSuccessMsg("If the email exists, a reset link has been sent.");
      setEmail("");
      setCooldown(30);
    } catch (err) {
      // Network fatal errors: still show uniform message (unless you prefer a separate fatal banner)
      setSuccessMsg("If the email exists, a reset link has been sent.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO title="Forgot Password | TradeSagePro" description="Recover access to your TradeSagePro account securely via email verification." canonical="https://tradesagepro.com/forgot-password" noindex nofollow />
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-white">Forgot Password</CardTitle>
              <CardDescription className="text-[hsl(210,9%,80%)]">Enter your email to receive a reset link.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-white">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-[hsl(226,26%,7%)] border-gray-600 text-white placeholder:text-gray-400 focus:border-[hsl(193,100%,50%)] focus:ring-[hsl(193,100%,50%)]"
                  />
                </div>

                {errorMsg && (
                  <div className="text-sm bg-red-600/20 border border-red-500/50 text-white rounded px-3 py-2">
                    {errorMsg}
                  </div>
                )}
                {successMsg && (
                  <div className="text-sm bg-green-600/20 border border-green-500/50 text-white rounded px-3 py-2">
                    {successMsg}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || cooldown > 0 || !isValidEmail(email)}
                  className="w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:bg-[hsl(193,90%,45%)] transition-all duration-300 font-semibold"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-[hsl(226,26%,7%)] border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    cooldown > 0 ? `Try again in ${formatMMSS(cooldown)}` : "Send reset link"
                  )}
                </Button>

                <div className="text-center text-sm mt-2">
                  <Link href="/login" className="text-[hsl(193,100%,55%)] hover:underline">Back to login</Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
